import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { joinMeeting } from "../apis/videoService";
import { useChimeVideo } from "../hooks/useChimeVideo";
import type { MeetingEndInfo } from "../services/chimeVideo";
import VideoBackgroundControls from "../components/VideoBackgroundControls";
import VideoExitConfirmation from "../components/VideoExitConfirmation";
import "./videoConsultant.css";

const VideoJoin = () => {
  const { meetingUuid } = useParams<{ meetingUuid: string }>();
  const navigate = useNavigate();
  const joinGenerationRef = useRef(0);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [endedMessage, setEndedMessage] = useState<string | null>(null);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const leavingRef = useRef(false);

  const {
    connectionState,
    isMeetingActive,
    isConnecting,
    remoteParticipantState,
    error,
    warning,
    isMuted,
    isVideoEnabled,
    isLocalVideoReady,
    cameraState,
    requiresAudioActivation,
    requiresVideoActivation,
    isRecording,
    initialize,
    startMeeting,
    stopMeeting,
    toggleMute,
    retryLocalVideo,
    enableRawVideo,
    closeVideo,
    switchCamera,
    resumeAudioPlayback,
    resumeVideoPlayback,
    setLocalVideoElement,
    setRemoteVideoElement,
    setAudioElement,
    setOnMeetingEnded,
  } = useChimeVideo();

  useEffect(() => {
    setOnMeetingEnded((endInfo: MeetingEndInfo) => {
      setEndedMessage(endInfo.message);
      void stopMeeting();
    });
    return () => setOnMeetingEnded(null);
  }, [setOnMeetingEnded, stopMeeting]);

  useEffect(() => {
    if (!meetingUuid) {
      setJoinError("缺少視訊會議識別碼");
      return;
    }

    const generation = ++joinGenerationRef.current;
    setJoinError(null);
    setEndedMessage(null);

    void (async () => {
      try {
        const joined = await joinMeeting({ meetingUuid });
        if (joinGenerationRef.current !== generation) return;
        await initialize(joined.meeting, joined.attendee, {
          participantRole: joined.participantRole,
          expiresAt: joined.expiresAt,
          recordingStarted: joined.recordingStarted,
        });
        if (joinGenerationRef.current !== generation) return;
        await startMeeting();
      } catch {
        if (joinGenerationRef.current === generation) {
          setJoinError("加入視訊會議失敗，請檢查連結或稍後重試。");
        }
      }
    })();

    return () => {
      if (joinGenerationRef.current === generation) {
        joinGenerationRef.current += 1;
      }
      void stopMeeting();
    };
  }, [initialize, meetingUuid, startMeeting, stopMeeting]);

  const handleLeave = async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setIsLeaving(true);
    setConfirmLeaveOpen(false);
    joinGenerationRef.current++;
    try {
      await stopMeeting();
      navigate(-1);
    } catch {
      leavingRef.current = false;
      setIsLeaving(false);
      setJoinError("離開通話失敗，請重試。");
    }
  };

  const remoteStatusText = {
    waiting: "等待諮詢團隊加入...",
    "present-no-video": "諮詢團隊已加入，尚未開啟攝影機",
    "video-paused": "網路頻寬不足，對方畫面暫停，聲音仍可使用",
    left: "諮詢團隊暫時離開，請稍候",
    "video-on": "",
  }[remoteParticipantState];

  if (endedMessage) {
    return (
      <div className="video-page">
        <div className="meeting-ended" aria-live="assertive">
          <p>{endedMessage}</p>
          <button onClick={() => navigate(-1)}>返回</button>
        </div>
      </div>
    );
  }

  return (
    <div className="video-page">
      <VideoExitConfirmation open={confirmLeaveOpen} busy={isLeaving} endsMeeting={false}
        onCancel={() => setConfirmLeaveOpen(false)} onConfirm={handleLeave} />
      {!isMeetingActive && !isConnecting && !error && !joinError && (
        <div className="connecting-container">
          <div className="spinner" />
          <p>準備加入視訊會議...</p>
        </div>
      )}
      {!isMeetingActive && isConnecting && (
        <div className="connecting-container">
          <div className="spinner" />
          <p>正在加入視訊會議...</p>
        </div>
      )}

      {isMeetingActive && (
        <>
          <audio ref={setAudioElement} autoPlay className="meeting-audio" />
          <div
            className={`meeting-status ${connectionState === "reconnecting" ? "reconnecting" : ""}`}
            aria-live="polite"
          >
            {connectionState === "reconnecting"
              ? "網路中斷，正在重新連線。保留畫面為非即時影像。"
              : remoteStatusText}
          </div>

          {isRecording && (
            <div className="recording-indicator">
              <span className="recording-dot" />
              <span>錄影中</span>
            </div>
          )}
          {requiresAudioActivation && (
            <div className="audio-activation" role="alert">
              <span>手機瀏覽器需要您手動開啟聲音。</span>
              <button type="button" onClick={resumeAudioPlayback}>
                點一下開啟聲音
              </button>
            </div>
          )}
          {requiresVideoActivation && (
            <div className="audio-activation" role="alert">
              <span>手機瀏覽器已暫停畫面播放。</span>
              <button type="button" onClick={resumeVideoPlayback}>
                點一下恢復畫面
              </button>
            </div>
          )}
          {warning && <div className="video-warning">{warning}</div>}

          <div className="video-container">
            <div className="remote-video-container">
              <video
                ref={setRemoteVideoElement}
                className="remote-video"
                autoPlay
                playsInline
              />
              {remoteParticipantState !== "video-on" && (
                <div className="remote-placeholder">
                  <span className="avatar">👨‍⚕️</span>
                  <span>{remoteStatusText}</span>
                </div>
              )}
            </div>

            <div
              className={`local-video-container ${
                !isLocalVideoReady ? "loading" : ""
              }`}
            >
              <video
                ref={setLocalVideoElement}
                className="local-video"
                data-background-mode={cameraState.mode}
                autoPlay
                playsInline
                muted
              />
              {!isLocalVideoReady && (
                <span className="local-video-status">
                  {cameraState.status === "loading" ? "攝影機準備中" : isVideoEnabled ? "攝影機尚未就緒" : "攝影機未開啟"}
                </span>
              )}
            </div>
          </div>

          <div className="video-controls" role="group" aria-label="通話控制">
            <button
              className={`control-btn ${isMuted ? "muted" : ""}`}
              onClick={toggleMute}
              title={isMuted ? "取消靜音" : "靜音"}
            >
              <i
                className={
                  isMuted ? "fas fa-microphone-slash" : "fas fa-microphone"
                }
              />
            </button>
            <VideoBackgroundControls cameraState={cameraState} isLocalVideoReady={isLocalVideoReady}
              connected={connectionState === "connected"} isReconnecting={connectionState === "reconnecting"}
              retryProtectedVideo={retryLocalVideo}
              enableRawVideo={enableRawVideo} closeVideo={closeVideo} switchCamera={switchCamera} />
            <button
              className="control-btn leave-call"
              onClick={() => setConfirmLeaveOpen(true)}
              disabled={isLeaving}
              aria-busy={isLeaving}
              aria-label={isLeaving ? "正在離開會議" : "離開會議"}
              title="離開會議"
            >
              <i className="fas fa-phone-slash" />
            </button>
          </div>
        </>
      )}

      {(error || joinError) && !isConnecting && (
        <div className="error-container">
          <p>{error || joinError}</p>
          <button onClick={() => window.location.reload()}>重試</button>
        </div>
      )}
    </div>
  );
};

export default VideoJoin;
